import { ProductType } from '@generated/gql/graphql';
import { BundleBasketData, SelectedServices } from '@src/types/bundle';

type CountServicesParams = {
  isEnergySelected: boolean;
  isBroadbandSelected: boolean;
  isMobileSelected: boolean;
  isMultiMobileSelected: boolean;
  isInsuranceSelected: boolean;
};

// coreServiceCount: energy + broadband + insurance + mobile, max 5 — mobile
// counts twice when more than one SIM is selected
const countServices = ({
  isEnergySelected,
  isBroadbandSelected,
  isMobileSelected,
  isMultiMobileSelected,
  isInsuranceSelected,
}: CountServicesParams) => {
  let coreServiceCount = 0;
  const nonEnergyServiceCount = [
    isBroadbandSelected,
    isMobileSelected,
    isMultiMobileSelected,
    isInsuranceSelected,
  ].filter(Boolean).length;

  coreServiceCount = nonEnergyServiceCount;

  if (isEnergySelected) {
    coreServiceCount++;
  }

  return { nonEnergyServiceCount, coreServiceCount };
};

const formatSelectedServices = (basket: Nullable<BundleBasketData>) => {
  const selectedServices: SelectedServices = {
    energy: false,
    broadband: false,
    mobile: false,
    insurance: false,
    cashback: false,
  };

  if (!basket) {
    return { selectedServices, nonEnergyServiceCount: 0, coreServiceCount: 0 };
  }

  const { energy, broadband, mobile, insurance, cashback } = basket;

  const isEnergySelected = !!energy?.selectedQuote;
  const isBroadbandSelected = !!broadband.yourService;
  const isMobileSelected = (mobile?.selectedSimCount ?? 0) > 0;
  const isMultiMobileSelected = (mobile?.selectedSimCount ?? 0) > 1;
  const isInsuranceSelected = insurance.selected;
  const isCashbackSelected = cashback.selected;

  selectedServices.energy = isEnergySelected;
  selectedServices.broadband = isBroadbandSelected;
  selectedServices.mobile = isMobileSelected;
  selectedServices.insurance = isInsuranceSelected;
  selectedServices.cashback = isCashbackSelected;

  const { nonEnergyServiceCount, coreServiceCount } = countServices({
    isEnergySelected,
    isBroadbandSelected,
    isMobileSelected,
    isMultiMobileSelected,
    isInsuranceSelected,
  });

  return { selectedServices, nonEnergyServiceCount, coreServiceCount };
};

const selectedServicesToProductTypeMap: Record<
  keyof SelectedServices,
  ProductType
> = {
  energy: ProductType.Energy,
  mobile: ProductType.Mobile,
  broadband: ProductType.Broadband,
  insurance: ProductType.Insurance,
  cashback: ProductType.CashbackCard,
};

const selectedServicesToProductType = (selectedServices: SelectedServices) => {
  return Object.keys(selectedServices).reduce((acc, key: string) => {
    const serviceKey = key as keyof SelectedServices;

    if (!selectedServices[serviceKey]) {
      return acc;
    }

    const productType = selectedServicesToProductTypeMap[serviceKey];

    if (productType) {
      acc.push(productType);
    }

    return acc;
  }, [] as ProductType[]);
};

export { formatSelectedServices, selectedServicesToProductType };
